import isReadonlyArray from './isReadonlyArray';
import isReadonlySet from './isReadonlySet';

declare const IMMUTABLE_MAP_SYMBOL: unique symbol;

export abstract class ImmutableSet<T> extends Set<T> {
  static readonly EMPTY = ImmutableSet.from<never>();

  static transmute<T>(map: ReadonlySet<T>): asserts map is ImmutableSet<T> {
    Object.setPrototypeOf(map, ImmutableSet.prototype);
  }

  static from<T>(
    ...args: ConstructorParameters<typeof Set<T>>
  ): ImmutableSet<T> {
    const set = new Set(...args);
    if (set.size === 0 && ImmutableSet.EMPTY != null) {
      return ImmutableSet.EMPTY;
    }
    ImmutableSet.transmute(set);
    return set;
  }

  declare readonly [IMMUTABLE_MAP_SYMBOL]: never;

  override delete(_value: T): boolean {
    throw new TypeError("You can't modify an immutable set!");
  }

  override add(_value: T): this {
    throw new TypeError("You can't modify an immutable set!");
  }

  override clear(): void {
    throw new TypeError("You can't modify an immutable set!");
  }
}

export default function createReadonlySet<T>(
  values?: Iterable<T> | ReadonlySet<T> | null,
): ReadonlySet<T> {
  let set;
  if (
    values == null ||
    (isReadonlyArray(values) && values.length === 0) ||
    (isReadonlySet(values) && values.size === 0) ||
    (set = ImmutableSet.from(values)).size === 0
  ) {
    set = ImmutableSet.EMPTY;
  }
  return set;
}
